import { motion } from "framer-motion";
import { Search, CalendarCheck, Sparkles } from "lucide-react";

/**
 * Three-step "How it works" strip.
 *
 * Kept intentionally quiet — most of the visual weight of the homepage lives
 * in the hero photo + featured coaches. This section is here to reassure a
 * first-time visitor that booking is actually easy.
 */
const STEPS = [
  {
    n: "01",
    Icon: Search,
    title: "Find your coach",
    body: "Search by sport, skill, or city — or scroll featured coaches. Every profile shows real reviews, price, and availability.",
  },
  {
    n: "02",
    Icon: CalendarCheck,
    title: "Book a session",
    body: "Pick a time that works and pay securely. No bundles, no subscriptions — just one session at a time until you're sure.",
  },
  {
    n: "03",
    Icon: Sparkles,
    title: "Show up and grow",
    body: "Meet online or in person. Rate your session afterwards — payouts release when the coach marks it complete.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works"
      className="py-24 md:py-32 bg-slate-900 text-white"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20 max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-white/10 text-[11px] uppercase tracking-[0.24em] font-semibold text-white/80">
            § How it works
          </div>
          <h2 className="mt-6 font-display font-medium text-[36px] sm:text-[44px] md:text-[56px] leading-[1.02] tracking-[-0.035em] max-w-[22ch]">
            From <span className="italic font-light text-white/70">"I want to try that"</span> to booked in a minute.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              data-testid={`how-step-${step.n}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-9 overflow-hidden"
            >
              <div className="absolute top-6 right-6 font-display text-white/10 text-[68px] leading-none tracking-tight select-none">
                {step.n}
              </div>
              <div className="w-11 h-11 rounded-2xl bg-white text-slate-900 grid place-items-center">
                <step.Icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <h3 className="mt-6 font-display text-2xl md:text-[26px] tracking-tight">
                {step.title}
              </h3>
              <p className="mt-3 text-white/70 text-[14.5px] leading-relaxed max-w-[32ch]">
                {step.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
